// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0

/* eslint-disable class-methods-use-this */
import {
  ApiException,
  Event,
  JsCantonError,
  JsGetActiveContractsResponse,
  JsGetUpdatesResponse,
  Update,
} from '@digitalasset/json-api-ts-client';
import { InitializedACSSet, SerializedACSContainer } from './ACSTypes';

type LogData = {
  info?: string;
  contractId?: string;
  offset?: number;
  type?: 'create' | 'archive';

  debugData?: unknown;
};

export class ACSContainerLogger {
  public log: string = '';

  public willTryToFetchMoreUpdates() {
    this.writeToLog(
      'Fetched updates were equal to max updates fetch limit. Will try to fetch more',
      null,
    );
  }

  public encounteredUpdateOfUnknownType(update: Update) {
    this.writeToLog('While extracting events got an update of unknown type', update);
  }

  public encounteredOlderEvent(
    context: string,
    currentOffset: number,
    encounteredOffset: number,
    event: Event,
  ) {
    this.writeToLog(`${context} encountered an event with offset older than current ACS.`, {
      currentOffset,
      encounteredOffset,
      event,
    });
  }

  public addedContractToACSSet(contractId: string, offset: number) {
    this.writeToLog('Added Contract to ACS Set', {
      contractId,
      offset,
    });
  }

  public removedContractFromACSSet(contractId: string, offset: number) {
    this.writeToLog('Removed Contract from ACS Set', {
      contractId,
      offset,
    });
  }

  public updatedACSSet(acsSet: InitializedACSSet) {
    this.writeToLog('Updated ACS set', {
      currentOffset: acsSet.acsOffset,
      contracts: this.convertResponsesToLogData(acsSet.acs),
    });
  }

  public fetchedEntireACS(acsSet: InitializedACSSet) {
    this.writeToLog('Fetched entire ACS state', {
      currentOffset: acsSet.acsOffset,
      contracts: this.convertResponsesToLogData(acsSet.acs),
    });
  }

  public fetchedACSUpdates(
    updates: JsGetUpdatesResponse[],
    fromOffset: number,
    toOffset: number,
    limit: number,
  ) {
    this.writeToLog('Fetched updates', {
      updatesCount: updates.length,
      updates: this.convertUpdatesToLogData(updates),
      fromOffset,
      toOffset,
      limit,
    });
  }

  public gotErrorDuringFetchingEntireACSUsingHTTP(error: ApiException<JsCantonError>) {
    this.writeToLog('Got ERROR during fetching entire ACS using HTTP', {
      error,
    });
  }

  public gotErrorDuringFetchingEntireACSUsingWebSockets(error: Error) {
    this.writeToLog('Got ERROR during fetching entire ACS using WebSockets', {
      error,
    });
  }

  public gotErrorDuringFetchingACSUpdatesUsingHTTP(error: Error) {
    this.writeToLog('Got ERROR during fetching ACS updates using HTTP', {
      error,
    });
  }

  public triedToFetchUpdatesWithLowerOffset(currentOffset: number, targetOffset: number) {
    this.writeToLog('Tried to fetch updates with offset lower than current', {
      currentOffset,
      targetOffset,
    });
  }

  public initializedFromPreviousContainerInstance(serializedContainer: SerializedACSContainer) {
    if (serializedContainer.log) {
      this.log = serializedContainer.log;
    } else {
      this.writeToLog('Initialized from previous ACS Container but it had no logs', null);
    }
  }

  private convertUpdatesToLogData(updates: JsGetUpdatesResponse[]): LogData[] {
    return updates.flatMap<LogData>((update) => {
      if ('Transaction' in update.update) {
        const transaction = update.update.Transaction;

        return (transaction.value.events ?? []).map<LogData>((event) => {
          if ('CreatedEvent' in event) {
            return {
              type: 'create',
              contractId: event.CreatedEvent.contractId,
              offset: event.CreatedEvent.offset,
            };
          }
          if ('ArchivedEvent' in event) {
            return {
              type: 'archive',
              contractId: event.ArchivedEvent.contractId,
              offset: event.ArchivedEvent.offset,
            };
          }

          return {
            info: 'Received unknown event type',
            debugData: event,
          };
        });
      }
      if ('OffsetCheckpoint' in update.update) {
        return {
          info: 'Offset checkpoint',
          offset: update.update.OffsetCheckpoint.value.offset,
        };
      }

      return {
        info: 'Received unknown update',
        debugData: update,
      };
    });
  }

  private convertResponsesToLogData(responses: JsGetActiveContractsResponse[]): LogData[] {
    return responses.map<LogData>((response) => {
      if ('JsActiveContract' in response.contractEntry) {
        return {
          contractId: response.contractEntry.JsActiveContract.createdEvent.contractId,
          offset: response.contractEntry.JsActiveContract.createdEvent.offset,
        };
      }
      return {
        info: `Failed to unpack contractId - no 'JsActiveContract' field.`,
        debugData: response,
      };
    });
  }

  private writeToLog(message: string, data: unknown) {
    const entry = JSON.stringify({
      date: new Date().toISOString(),
      message,
      data,
    });

    if (this.log.length > 0) {
      this.log = `${entry},${this.log}`;
    } else {
      this.log = entry;
    }
  }
}
